import { ExternalLink } from 'lucide-react';
import {
  STARLINK_TRADE_CAVEAT,
  STARLINK_TRADE_CORRIDORS,
  STARLINK_TRADE_KPIS,
  STARLINK_TRADE_SHIPMENTS,
  TRADE_FAMILY_LABEL,
  corridorChange,
  formatUsdMillions,
  type TradeShipment,
} from '@/components/value-chain/starlinkTrade';

const HERO_BL = 'S2601869295';

export function StarlinkTradeEvidence() {
  const hero = STARLINK_TRADE_SHIPMENTS.find((row) => row.blNumber === HERO_BL);
  const sample = STARLINK_TRADE_SHIPMENTS.find((row) => row.valueUsd != null);

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900">Hardware landings (customs)</h2>
          <p className="mt-0.5 max-w-3xl text-[12px] leading-relaxed text-slate-500">
            Independent file after the 10-Q mix. Kits named Starlink on public bills, plus US HS 851762
            corridors as a noisy upper bound.
          </p>
        </div>
        <p className="max-w-sm text-right text-[10px] leading-relaxed text-slate-400">{STARLINK_TRADE_CAVEAT}</p>
      </div>

      <dl className="mb-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {STARLINK_TRADE_KPIS.map((kpi) => (
          <div key={kpi.label} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
            <dt className="text-[11px] text-slate-500">{kpi.label}</dt>
            <dd className="mt-0.5 text-[20px] leading-none font-semibold tracking-tight text-slate-900">
              {kpi.value}
            </dd>
            <p className="mt-1 text-[10px] text-slate-400">{kpi.detail}</p>
          </div>
        ))}
      </dl>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.9fr)]">
        <div className="min-w-0 overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-[12px]">
            <thead>
              <tr className="border-b border-slate-200 text-[11px] tracking-wide text-slate-400 uppercase">
                <th className="pb-2 pr-3 font-medium">Date</th>
                <th className="pb-2 pr-3 font-medium">Dest.</th>
                <th className="pb-2 pr-3 font-medium">SKU</th>
                <th className="pb-2 pr-3 font-medium">Qty</th>
                <th className="pb-2 font-medium">Line</th>
              </tr>
            </thead>
            <tbody>
              {STARLINK_TRADE_SHIPMENTS.map((row) => (
                <ShipmentRow key={`${row.date}-${row.blNumber}-${row.description}`} row={row} />
              ))}
            </tbody>
          </table>
        </div>

        <div className="space-y-3">
          {hero ? (
            <article className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-[11px] font-medium text-slate-400">Hero bill</p>
              <p className="mt-1 text-[18px] leading-none font-semibold tracking-tight text-slate-900">
                1,024 Mini kits
              </p>
              <p className="mt-1.5 text-[12px] leading-relaxed text-slate-600">
                {hero.destination} · {hero.date} · {hero.blNumber} · UTA-231. One inbound, not a run-rate.
              </p>
            </article>
          ) : null}
          {sample ? (
            <article className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-[11px] font-medium text-slate-400">Consumer kit FOB (Quito air)</p>
              <p className="mt-1 text-[18px] leading-none font-semibold tracking-tight text-slate-900">
                {sample.valueUsd != null ? `$${sample.valueUsd.toFixed(2)}` : '—'}
              </p>
              <p className="mt-1.5 text-[12px] leading-relaxed text-slate-600">
                {`Freight $${sample.freightUsd?.toFixed(2)} on the same bill. Dish is cheap; freight can exceed FOB. Connectivity Products, not ARPU.`}
              </p>
            </article>
          ) : null}
          <article className="rounded-xl border border-slate-200 p-3">
            <p className="text-[12px] font-semibold text-slate-800">US HS 851762 exports</p>
            <p className="mt-0.5 text-[10px] text-slate-400">
              UN Comtrade · reporter 842 · 2022 → 2024. Category dollars, not Starlink-only.
            </p>
            <table className="mt-2 w-full text-left text-[12px]">
              <tbody>
                {STARLINK_TRADE_CORRIDORS.map((row) => {
                  const change = corridorChange(row);
                  return (
                    <tr key={row.partner} className="border-b border-slate-100 last:border-0">
                      <td className="py-1.5 text-slate-700">
                        {row.partner}
                        {row.labeled ? (
                          <span className="ml-1.5 rounded bg-blue-50 px-1 py-0.5 text-[9px] font-medium text-blue-700">
                            labeled kits
                          </span>
                        ) : null}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-slate-500">
                        {formatUsdMillions(row.y2022)}
                      </td>
                      <td className="py-1.5 text-right tabular-nums font-medium text-slate-900">
                        {formatUsdMillions(row.y2024)}
                      </td>
                      <td
                        className={`py-1.5 text-right text-[11px] font-semibold ${
                          change.down ? 'text-rose-500' : 'text-emerald-600'
                        }`}
                      >
                        {change.label}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="mt-2 text-[10px] leading-relaxed text-slate-400">
              Ecuador HS dollars eased while Mini volume is visible on air bills. Do not read the corridor as kit
              units. World HS 851762 US exports were $22.5b in 2024.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}

function ShipmentRow({ row }: { row: TradeShipment }) {
  const hero = row.blNumber === HERO_BL;
  return (
    <tr className={`border-b border-slate-100 ${hero ? 'bg-slate-50' : ''}`}>
      <td className="py-2 pr-3 whitespace-nowrap tabular-nums text-slate-500">{row.date ?? '—'}</td>
      <td className="py-2 pr-3 text-slate-800">{row.destination}</td>
      <td className="py-2 pr-3 whitespace-nowrap text-slate-700">{TRADE_FAMILY_LABEL[row.family]}</td>
      <td className="py-2 pr-3 tabular-nums font-medium text-slate-900">
        {row.quantity != null ? row.quantity.toLocaleString() : '—'}
      </td>
      <td className="py-2">
        <span className="block text-slate-700">{row.description}</span>
        <a
          href={row.sourceUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-blue-700 hover:text-blue-900"
        >
          {row.sourceLabel}
          {row.blNumber ? ` · ${row.blNumber}` : ''}
          <ExternalLink className="h-3 w-3" />
        </a>
      </td>
    </tr>
  );
}
